import maplibregl from 'maplibre-gl'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import { db } from '../../database/schema.js'
import { timelineService, type TimelineListener } from '../../services/timeline/timeline-service.js'
import { logger } from '../../utils/logger.js'
import type {
  INatObservation,
  TaxonEntry,
  TaxonResult,
  WildlifeCacheEntry,
} from './wildlife-types.js'

const log = logger.create('inaturalist')

const API_BASE = 'https://api.inaturalist.org/v1'
const MIN_REQUEST_INTERVAL_MS = 100
const CACHE_TTL_MS = 24 * 60 * 60 * 1000

const SOURCE_ID = 'inat-observations'
const HEATMAP_LAYER = 'inat-heatmap'
const CLUSTER_LAYER = 'inat-clusters'
const CLUSTER_COUNT_LAYER = 'inat-cluster-count'
const POINTS_LAYER = 'inat-points'

const MIN_ZOOM = 5
const EMPTY_GEOJSON: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

const TAXON_ICONS: Record<string, { icon: string; color: string }> = {
  Mammalia: { icon: 'pets', color: '#16a34a' },
  Aves: { icon: 'flutter', color: '#2563eb' },
  Reptilia: { icon: 'bug_report', color: '#ea580c' },
  Amphibia: { icon: 'water', color: '#0d9488' },
}
const DEFAULT_ICON = { icon: 'eco', color: '#65a30d' }

let lastRequestTime = 0

async function throttledFetch(url: string): Promise<Response> {
  const now = Date.now()
  const elapsed = now - lastRequestTime
  if (elapsed < MIN_REQUEST_INTERVAL_MS) {
    await new Promise(resolve => setTimeout(resolve, MIN_REQUEST_INTERVAL_MS - elapsed))
  }
  lastRequestTime = Date.now()
  return fetch(url)
}

function quantizeBounds(
  south: number,
  west: number,
  north: number,
  east: number
): { south: number; west: number; north: number; east: number } {
  const step = 0.5
  return {
    south: Math.floor(south / step) * step,
    west: Math.floor(west / step) * step,
    north: Math.ceil(north / step) * step,
    east: Math.ceil(east / step) * step,
  }
}

function buildCacheKey(
  qBounds: { south: number; west: number; north: number; east: number },
  taxonIds: readonly number[],
  d1: string,
  d2: string
): string {
  const sorted = [...taxonIds].sort((a, b) => a - b)
  return `inat:${qBounds.south},${qBounds.west},${qBounds.north},${qBounds.east}:${sorted.join(',')}:${d1}:${d2}`
}

function timelineDateParams(): { d1: string; d2: string; params: string } {
  const state = timelineService.getState()
  const d1 = state.startTime.toISOString().split('T')[0]
  const d2 = state.endTime.toISOString().split('T')[0]
  return { d1, d2, params: `&d1=${d1}&d2=${d2}` }
}

function normalizeObservation(raw: Record<string, unknown>): INatObservation | null {
  const taxon = raw.taxon as Record<string, unknown> | undefined
  const geojson = raw.geojson as { coordinates?: [number, number] } | undefined
  if (!geojson?.coordinates || !taxon) return null

  const photos = raw.photos as Array<{ url?: string }> | undefined
  let photoUrl: string | undefined
  let thumbUrl: string | undefined
  if (photos && photos.length > 0 && photos[0].url) {
    const url = photos[0].url
    photoUrl = url.replace('square', 'medium')
    thumbUrl = url
  }

  return {
    id: raw.id as number,
    commonName: (taxon.preferred_common_name as string) || (taxon.name as string) || 'Unknown',
    scientificName: (taxon.name as string) || '',
    iconicTaxon: (taxon.iconic_taxon_name as string) || '',
    lat: geojson.coordinates[1],
    lon: geojson.coordinates[0],
    observedOn: (raw.observed_on as string) || '',
    photoUrl,
    thumbUrl,
    qualityGrade: (raw.quality_grade as string) || '',
    observerLogin: ((raw.user as Record<string, unknown>)?.login as string) || '',
    uri: (raw.uri as string) || '',
  }
}

function registerTaxonIcon(
  map: maplibregl.Map,
  imageId: string,
  iconName: string,
  color: string
): void {
  if (map.hasImage(imageId)) return

  const dpr = window.devicePixelRatio || 1
  const size = 24
  const canvas = document.createElement('canvas')
  canvas.width = size * dpr
  canvas.height = size * dpr
  const ctx = canvas.getContext('2d')!
  ctx.scale(dpr, dpr)

  const cx = size / 2
  const cy = size / 2
  const r = size / 2 - 2

  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fillStyle = color
  ctx.fill()
  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = 1.5
  ctx.stroke()

  if (document.fonts.check('12px "Material Symbols Outlined"')) {
    ctx.font = '12px "Material Symbols Outlined"'
    ctx.fillStyle = '#ffffff'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(iconName, cx, cy)
  }

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  map.addImage(imageId, imageData, { pixelRatio: dpr })
}

class INaturalistService {
  private map: maplibregl.Map | null = null
  private _enabled = false
  private _taxonEntries: TaxonEntry[] = []
  private _timeFilterEnabled = false
  private moveEndHandler: (() => void) | null = null
  private pendingUpdate: number | null = null
  private timelineListener: TimelineListener | null = null
  private lastTimelineKey = ''

  get enabled(): boolean {
    return this._enabled
  }

  get currentTaxonEntries(): readonly TaxonEntry[] {
    return this._taxonEntries
  }

  get currentTaxonIds(): readonly number[] {
    return this._taxonEntries.map(t => t.id)
  }

  get timeFilterEnabled(): boolean {
    return this._timeFilterEnabled
  }

  setTimeFilterEnabled(enabled: boolean): void {
    this._timeFilterEnabled = enabled
    if (this._enabled) this.updateData()
  }

  register(map: maplibregl.Map): void {
    this.map = map
    this.moveEndHandler = () => this.onMoveEnd()
    this.map.on('moveend', this.moveEndHandler)

    this.timelineListener = () => this.onTimelineChanged()
    timelineService.addListener(this.timelineListener)

    for (const [taxon, conf] of Object.entries(TAXON_ICONS)) {
      registerTaxonIcon(map, `inat-icon-${taxon}`, conf.icon, conf.color)
    }
    registerTaxonIcon(map, 'inat-icon-default', DEFAULT_ICON.icon, DEFAULT_ICON.color)

    this.map.on('mouseenter', POINTS_LAYER, () => {
      if (this.map) this.map.getCanvas().style.cursor = 'pointer'
    })
    this.map.on('mouseleave', POINTS_LAYER, () => {
      if (this.map) this.map.getCanvas().style.cursor = ''
    })
  }

  unregister(): void {
    if (this.pendingUpdate) {
      cancelAnimationFrame(this.pendingUpdate)
      this.pendingUpdate = null
    }
    if (this._enabled) this.disable()
    if (this.map && this.moveEndHandler) {
      this.map.off('moveend', this.moveEndHandler)
    }
    if (this.timelineListener) {
      timelineService.removeListener(this.timelineListener)
      this.timelineListener = null
    }
    this.map = null
  }

  enable(): void {
    if (!this.map || this._enabled) return
    this._enabled = true
    this.addLayers()
    this.updateData()
  }

  disable(): void {
    if (!this.map) return
    this._enabled = false
    this.removeLayers()
  }

  toggle(): void {
    if (this._enabled) this.disable()
    else this.enable()
  }

  reAdd(): void {
    if (!this.map || !this._enabled) return
    if (!this.map.getSource(SOURCE_ID)) {
      this.addLayers()
      this.updateData()
    }
  }

  setTaxonFilter(entries: TaxonEntry[]): void {
    this._taxonEntries = entries
    if (this._enabled) {
      this.clearSourceData()
      this.updateData()
    }
  }

  async searchTaxa(query: string): Promise<TaxonResult[]> {
    if (query.length < 2) return []
    try {
      const resp = await throttledFetch(
        `${API_BASE}/taxa/autocomplete?q=${encodeURIComponent(query)}&per_page=10&is_active=true`
      )
      if (!resp.ok) return []
      const data = await resp.json()
      return (data.results as Record<string, unknown>[]).map(r => ({
        id: r.id as number,
        name: (r.name as string) || '',
        commonName: (r.preferred_common_name as string) || (r.name as string) || '',
        iconicTaxon: (r.iconic_taxon_name as string) || '',
        observationCount: (r.observations_count as number) || 0,
        photoUrl: (r.default_photo as Record<string, unknown> | undefined)?.medium_url as
          | string
          | undefined,
      }))
    } catch (err) {
      log.warn('Taxa search failed', err)
      return []
    }
  }

  async queryFeaturesAtPoint(point: maplibregl.Point): Promise<INatObservation[]> {
    if (!this.map || !this._enabled) return []

    const hits = this.map.getLayer(POINTS_LAYER)
      ? this.map.queryRenderedFeatures(point, { layers: [POINTS_LAYER] })
      : []

    const results: INatObservation[] = []
    for (const hit of hits) {
      if (hit.properties) {
        const props = hit.properties as Record<string, unknown>
        results.push({
          id: props.id as number,
          commonName: props.commonName as string,
          scientificName: props.scientificName as string,
          iconicTaxon: props.iconicTaxon as string,
          lat: props.lat as number,
          lon: props.lon as number,
          observedOn: props.observedOn as string,
          photoUrl: props.photoUrl as string | undefined,
          thumbUrl: props.thumbUrl as string | undefined,
          qualityGrade: props.qualityGrade as string,
          observerLogin: props.observerLogin as string,
          uri: props.uri as string,
        })
      }
    }

    return results
  }

  private addLayers(): void {
    if (!this.map) return

    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: EMPTY_GEOJSON,
        cluster: true,
        clusterMaxZoom: 12,
        clusterRadius: 50,
      })
    }

    if (!this.map.getLayer(HEATMAP_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: HEATMAP_LAYER,
          type: 'heatmap',
          source: SOURCE_ID,
          minzoom: MIN_ZOOM,
          maxzoom: 10,
          paint: {
            'heatmap-weight': 1,
            'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 5, 0.5, 10, 1.5],
            'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 5, 4, 10, 15],
            'heatmap-color': [
              'interpolate',
              ['linear'],
              ['heatmap-density'],
              0,
              'rgba(22, 163, 74, 0)',
              0.2,
              'rgba(22, 163, 74, 0.3)',
              0.4,
              'rgba(101, 163, 13, 0.5)',
              0.6,
              'rgba(234, 179, 8, 0.7)',
              0.8,
              'rgba(234, 88, 12, 0.85)',
              1,
              'rgba(220, 38, 38, 1)',
            ],
            'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 8, 0.8, 10, 0],
          },
        },
        'wildlife-inat'
      )
    }

    if (!this.map.getLayer(CLUSTER_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: CLUSTER_LAYER,
          type: 'circle',
          source: SOURCE_ID,
          filter: ['has', 'point_count'],
          minzoom: 8,
          paint: {
            'circle-color': '#16a34a',
            'circle-opacity': 0.7,
            'circle-radius': ['step', ['get', 'point_count'], 15, 10, 20, 50, 25],
          },
        },
        'wildlife-inat'
      )
    }

    if (!this.map.getLayer(CLUSTER_COUNT_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: CLUSTER_COUNT_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          filter: ['has', 'point_count'],
          minzoom: 8,
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-size': 12,
          },
          paint: {
            'text-color': '#ffffff',
          },
        },
        'wildlife-inat'
      )
    }

    if (!this.map.getLayer(POINTS_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: POINTS_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          filter: ['!', ['has', 'point_count']],
          minzoom: 8,
          layout: {
            'icon-image': [
              'match',
              ['get', 'iconicTaxon'],
              'Mammalia',
              'inat-icon-Mammalia',
              'Aves',
              'inat-icon-Aves',
              'Reptilia',
              'inat-icon-Reptilia',
              'Amphibia',
              'inat-icon-Amphibia',
              'inat-icon-default',
            ],
            'icon-size': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 14, 1],
            'icon-anchor': 'center',
            'icon-allow-overlap': true,
          },
        },
        'wildlife-inat'
      )
    }
  }

  private removeLayers(): void {
    if (!this.map) return
    for (const layerId of [POINTS_LAYER, CLUSTER_COUNT_LAYER, CLUSTER_LAYER, HEATMAP_LAYER]) {
      if (this.map.getLayer(layerId)) this.map.removeLayer(layerId)
    }
    if (this.map.getSource(SOURCE_ID)) this.map.removeSource(SOURCE_ID)
  }

  private clearSourceData(): void {
    if (!this.map) return
    const source = this.map.getSource<maplibregl.GeoJSONSource>(SOURCE_ID)
    if (source) source.setData(EMPTY_GEOJSON)
  }

  private onMoveEnd(): void {
    if (!this._enabled) return
    if (this.pendingUpdate) cancelAnimationFrame(this.pendingUpdate)
    this.pendingUpdate = requestAnimationFrame(() => {
      this.pendingUpdate = null
      this.updateData()
    })
  }

  private onTimelineChanged(): void {
    if (!this._enabled || !this._timeFilterEnabled) return
    const { d1, d2 } = timelineDateParams()
    const key = `${d1}:${d2}`
    if (key === this.lastTimelineKey) return
    this.lastTimelineKey = key
    this.updateData()
  }

  private updateData(): void {
    if (!this.map) return
    const bounds = this.map.getBounds()
    const qBounds = quantizeBounds(
      bounds.getSouth(),
      bounds.getWest(),
      bounds.getNorth(),
      bounds.getEast()
    )

    const { d1, d2 } = this._timeFilterEnabled ? timelineDateParams() : { d1: '', d2: '' }
    const cacheKey = buildCacheKey(qBounds, this.currentTaxonIds, d1, d2)

    this.getCachedOrFetch(cacheKey, qBounds)
      .then(observations => {
        if (!this.map) return
        const source = this.map.getSource<maplibregl.GeoJSONSource>(SOURCE_ID)
        if (!source) return

        const fc: GeoJSON.FeatureCollection = {
          type: 'FeatureCollection',
          features: observations.map(obs => ({
            type: 'Feature' as const,
            geometry: { type: 'Point' as const, coordinates: [obs.lon, obs.lat] },
            properties: obs as unknown as GeoJSON.GeoJsonProperties,
          })),
        }
        source.setData(fc)
      })
      .catch(err => {
        log.warn('Failed to update iNaturalist data', err)
      })
  }

  private async getCachedOrFetch(
    cacheKey: string,
    qBounds: { south: number; west: number; north: number; east: number }
  ): Promise<INatObservation[]> {
    try {
      const cached = await db.wildlifeCache.get(cacheKey)
      if (cached) {
        const age = Date.now() - new Date(cached.fetchedAt).getTime()
        if (age < CACHE_TTL_MS) return cached.data as INatObservation[]
      }
    } catch {
      // IndexedDB not available
    }

    const observations = await this.fetchObservations(qBounds)

    try {
      const entry: WildlifeCacheEntry = {
        key: cacheKey,
        dataType: 'inat',
        data: observations,
        fetchedAt: new Date().toISOString(),
      }
      await db.wildlifeCache.put(entry)
    } catch {
      // IndexedDB write failed
    }

    return observations
  }

  private async fetchObservations(qBounds: {
    south: number
    west: number
    north: number
    east: number
  }): Promise<INatObservation[]> {
    const taxonIds = this.currentTaxonIds
    const taxonParam = taxonIds.length > 0 ? `taxon_id=${taxonIds.join(',')}&` : ''
    const dateParams = this._timeFilterEnabled ? timelineDateParams().params : ''
    const results: INatObservation[] = []
    let page = 1
    const maxPages = 5

    while (page <= maxPages) {
      const url =
        `${API_BASE}/observations?` +
        taxonParam +
        `quality_grade=research` +
        `&swlat=${qBounds.south}&swlng=${qBounds.west}` +
        `&nelat=${qBounds.north}&nelng=${qBounds.east}` +
        `&per_page=200&page=${page}` +
        `&fields=id,taxon,geojson,observed_on,photos,quality_grade,user,uri` +
        dateParams

      try {
        const resp = await throttledFetch(url)
        if (!resp.ok) break

        const data = await resp.json()
        const rawResults = data.results as Record<string, unknown>[]
        if (!rawResults || rawResults.length === 0) break

        for (const raw of rawResults) {
          const obs = normalizeObservation(raw)
          if (obs) results.push(obs)
        }

        if (rawResults.length < 200) break
        page++
      } catch (err) {
        log.warn('iNaturalist fetch failed', err)
        break
      }
    }

    return results
  }
}

export const iNaturalistService = new INaturalistService()
